// Tela de aceite de convite — pública (não exige login). Dois fluxos
// distintos baseados em `vehicleType`:
//
// - `org_support` (Step 4 do wizard): "link aberto". Motorista informa
//   só o email que vai usar pra entrar no app WindMaps. Aprovação é
//   automática (organização criou o cadastro intencionalmente).
//
// - `team_support` (Step 5): fluxo tradicional. Construtor preenche
//   dados da operadora (nome, slug, cor, responsável) e entra na fila
//   de aprovação manual da organização.
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/api/client';
import { commonT } from '@/i18n';
import { eventsInviteT } from '@/i18n/messages/eventsInvite';
import { LanguageSelect } from '@/components/LanguageSelect';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

interface InviteSnapshot {
  id: string;
  email: string;
  name: string;
  vehicleType: 'org_support' | 'team_support' | 'course_marker';
  status: 'pending' | 'accepted' | 'approved' | 'rejected' | 'expired';
  expiresAt: string;
  eventName: string;
}

/// Moldura das telas do convite, com o seletor de idioma no canto: quem
/// recebe o link pode não ler português (equipe de apoio estrangeira). O
/// recarregamento da troca mantém o `?token=` da URL.
function InvitePage({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn('relative min-h-screen flex items-center justify-center px-4', className)}>
      <LanguageSelect className="absolute right-4 top-3 text-muted-foreground" />
      {children}
    </div>
  );
}

export function InviteAcceptPage() {
  const t = eventsInviteT();
  const common = commonT();
  const [search] = useSearchParams();
  const token = search.get('token') ?? '';

  const [snapshot, setSnapshot] = useState<InviteSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);

  // Campos do fluxo team_support
  const [operatorName, setOperatorName] = useState('');
  const [slug, setSlug] = useState('');
  const [colorHex, setColorHex] = useState('#0EA5E9');
  const [contactName, setContactName] = useState('');

  // Campo do fluxo org_support (só email)
  const [driverEmail, setDriverEmail] = useState('');

  useEffect(() => {
    if (!token) {
      setError(eventsInviteT().missingToken);
      setLoading(false);
      return;
    }
    api<InviteSnapshot>(`/api/invites/by-token/${token}`)
      .then((res) => {
        setSnapshot(res);
        setOperatorName(res.name);
        setContactName(res.name);
      })
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  // org_support (apoio da organização) e course_marker (equipe de raia)
  // seguem o mesmo aceite "aberto" no backend: só email, auto-aprova. O
  // frontend tem que espelhar — senão course_marker cai no form de
  // construtora e envia sem email → backend rejeita com 400 invalid_email.
  const isOpenInvite =
    snapshot?.vehicleType === 'org_support' || snapshot?.vehicleType === 'course_marker';
  const isCourseMarker = snapshot?.vehicleType === 'course_marker';
  const openInviteNoun = isCourseMarker ? t.nounCourseTeam : t.nounSupport;
  const openInviteTitle = isCourseMarker ? t.titleCourseTeam : t.titleSupport;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const body = isOpenInvite
        ? { email: driverEmail }
        : { operatorName, slug, colorHex, contactName };
      await api(`/api/invites/by-token/${token}/accept`, {
        method: 'POST',
        body,
      });
      setAccepted(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  if (!snapshot || error) {
    return (
      <InvitePage>
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>{t.invalidTitle}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-destructive">{error ?? t.notFound}</p>
            <Button asChild variant="outline" className="mt-4 w-full sm:w-auto">
              <Link to="/">{common.back}</Link>
            </Button>
          </CardContent>
        </Card>
      </InvitePage>
    );
  }

  if (accepted) {
    return (
      <InvitePage>
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>{isOpenInvite ? t.allSetTitle : t.acceptedTitle}</CardTitle>
            <CardDescription>
              {isOpenInvite
                ? t.openAcceptedBody(driverEmail, snapshot.eventName, openInviteNoun, snapshot.name)
                : t.teamSentBody}
            </CardDescription>
          </CardHeader>
        </Card>
      </InvitePage>
    );
  }

  if (snapshot.status !== 'pending' && snapshot.status !== 'accepted') {
    return (
      <InvitePage>
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>{t.statusTitle[snapshot.status]}</CardTitle>
            <CardDescription>{t.unavailable}</CardDescription>
          </CardHeader>
        </Card>
      </InvitePage>
    );
  }

  if (isOpenInvite) {
    return (
      <InvitePage className="py-10">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <CardTitle>{openInviteTitle} {snapshot.name}</CardTitle>
            <CardDescription>{t.openBody(snapshot.eventName, snapshot.name)}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="driverEmail">{t.yourEmail}</Label>
                <Input
                  id="driverEmail"
                  type="email"
                  value={driverEmail}
                  onChange={(e) => setDriverEmail(e.target.value)}
                  placeholder={t.emailPlaceholder}
                  required
                  autoComplete="email"
                  className="h-11"
                />
                <p className="text-xs text-muted-foreground">{t.emailHint}</p>
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" disabled={submitting || !driverEmail.trim()} className="w-full">
                {submitting ? <Spinner className="mr-2" /> : null}
                {common.confirm}
              </Button>
            </form>
          </CardContent>
        </Card>
      </InvitePage>
    );
  }

  return (
    <InvitePage className="py-10">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>{t.teamTitle(snapshot.eventName)}</CardTitle>
          <CardDescription>{t.teamBody(snapshot.name)}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="opName">{t.teamNameLabel}</Label>
              <Input id="opName" value={operatorName} onChange={(e) => setOperatorName(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="opSlug">{t.slugLabel}</Label>
              <Input
                id="opSlug"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase())}
                placeholder={t.slugPlaceholder}
              />
              <p className="text-xs text-muted-foreground">{t.slugHint}</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="opColor">{t.teamColorLabel}</Label>
              <div className="flex items-center gap-2">
                <input
                  id="opColor"
                  type="color"
                  value={colorHex}
                  onChange={(e) => setColorHex(e.target.value.toUpperCase())}
                  className="h-11 w-16 rounded-md border bg-background"
                />
                <Input value={colorHex} onChange={(e) => setColorHex(e.target.value)} className="font-mono" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="opContact">{t.contactNameLabel}</Label>
              <Input id="opContact" value={contactName} onChange={(e) => setContactName(e.target.value)} required />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" disabled={submitting} className="w-full">
              {submitting ? <Spinner className="mr-2" /> : null}
              {t.acceptInvite}
            </Button>
          </form>
        </CardContent>
      </Card>
    </InvitePage>
  );
}
